import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { XStack } from '@/components/ui/layout/layout';

import { ProviderIcon } from '../components/provider-icon';
import {
  NEW_PERSON_NAME,
  WIKI_HAND_PROVIDERS,
  WIKI_PROVIDER_LABELS,
  WIKI_PROVIDERS,
  type WikiProvider,
} from '../people-wiki.types';

import {
  accountConflict,
  useAddAccountMutation,
  useMergePeopleMutation,
} from './edits.query';

const VALUE_PLACEHOLDERS: Partial<Record<WikiProvider, string>> = {
  email: 'name@example.com',
  phone: '+1 415 555 1234',
  linkedin: 'linkedin.com/in/name',
  github: '@handle',
  x: '@handle',
  calendly: 'calendly.com/name',
  website: 'example.com',
};

const accountNoun = (provider: WikiProvider): string => {
  switch (provider) {
    case WIKI_PROVIDERS.email:
      return 'email';
    case WIKI_PROVIDERS.phone:
      return 'number';
    case WIKI_PROVIDERS.website:
      return 'website';
    default:
      return 'handle';
  }
};

/** A row to type in one account: which app, then the address, number, handle or link. When the account is on
 * another person, the row offers to merge the two instead. */
export const AddAccountForm = ({
  person,
  onClose,
}: {
  person: { id: string; name: string };
  onClose: () => void;
}) => {
  const [provider, setProvider] = useState<WikiProvider>(WIKI_PROVIDERS.email);
  const [value, setValue] = useState('');
  const {
    mutate: addAccount,
    error,
    isPending: isAdding,
  } = useAddAccountMutation(person.id);
  const { mutate: merge, isPending: isMerging } = useMergePeopleMutation(
    person.id
  );
  const account = { provider, value: value.trim() };
  const conflict = accountConflict(error);

  if (conflict?.isSelf) {
    // A merge would put this page's accounts, facts and notes on the user's own page
    return (
      <XStack wrap className="basis-full items-center gap-2 text-sm">
        <span className="text-foreground-muted">
          This {accountNoun(provider)} is yours.
        </span>
        <Button size="xs" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </XStack>
    );
  }

  if (conflict) {
    // The page that remains keeps its name, so a page that is still called "New person" goes second
    const isNewPage = person.name === NEW_PERSON_NAME;
    const personIds = isNewPage
      ? [conflict.personId, person.id]
      : [person.id, conflict.personId];
    return (
      <XStack wrap className="basis-full items-center gap-2 text-sm">
        <span className="text-foreground-muted">
          This {accountNoun(provider)} is on {conflict.name}. Merge{' '}
          {isNewPage ? 'this page' : conflict.name} into{' '}
          {isNewPage ? conflict.name : person.name}?
        </span>
        <Button
          size="xs"
          isLoading={isMerging}
          onClick={() => merge({ personIds, account }, { onSuccess: onClose })}
        >
          Merge
        </Button>
        <Button
          size="xs"
          variant="ghost"
          disabled={isMerging}
          onClick={onClose}
        >
          Cancel
        </Button>
      </XStack>
    );
  }

  return (
    <form
      className="flex basis-full items-center gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (account.value !== '') addAccount(account, { onSuccess: onClose });
      }}
    >
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          aria-label="Account type"
          className="h-8 shrink-0 gap-1.5 text-xs"
          disabled={isAdding}
        >
          <ProviderIcon provider={provider} />
          <span>{WIKI_PROVIDER_LABELS[provider]}</span>
          <Icons.chevronDown className="size-3 shrink-0" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start" className="max-h-72">
          <DropdownMenu.RadioGroup
            value={provider}
            onValueChange={(next) => setProvider(next as WikiProvider)}
          >
            {WIKI_HAND_PROVIDERS.map((handProvider) => (
              <DropdownMenu.RadioItem
                key={handProvider}
                value={handProvider}
                className="gap-2 pr-8"
              >
                <ProviderIcon provider={handProvider} />
                {WIKI_PROVIDER_LABELS[handProvider]}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      <div className="min-w-0 max-w-xs flex-1">
        <Input
          inputSize="sm"
          aria-label={`${WIKI_PROVIDER_LABELS[provider]} account`}
          placeholder={VALUE_PLACEHOLDERS[provider]}
          value={value}
          maxLength={512}
          disabled={isAdding}
          autoFocus
          onChange={(event) => setValue(event.target.value)}
        />
      </div>
      <Button
        type="submit"
        size="xs"
        isLoading={isAdding}
        disabled={account.value === ''}
      >
        Add
      </Button>
      <Button size="xs" variant="ghost" disabled={isAdding} onClick={onClose}>
        Cancel
      </Button>
    </form>
  );
};
